/* eslint-disable ally-rules/form-has-method */
import * as React from 'react';
import styled from 'styled-components';
import { Form, Forms } from '@ally/forms';
import { muiLightbulb } from '@ally/metronome-icons';
import { Box, Button, Icon, Space, TextBody } from '@ally/metronome-ui';
import { HowToSetUpPushApproveModal } from '@ally/shared-push-approve-modal';
import { AriaLive } from '@ally/use-aria-live';
import { maskBy } from '../../../../../Utils';
import { TrackingEvent } from '../../../../../constants';
import { LoginHeading } from '../../../Layout/Layout';
import { DeliveryMethodsOptions } from './DeliveryMethodsOptions';
import { PushTextOrVoiceSelection } from './PushTextOrVoiceSelection';
// channelIndex is a sequential index, but its only distinct for the given targetIdentifier (sms | email)
const getDeliveryOptionId = ({ _channel, _channelIndex }) => `${_channel}-${_channelIndex}`;
const maskTarget = (target, getOptionType) => {
    const { _description: description, _channel: channel } = target;
    const type = getOptionType(channel);
    return maskBy[type](description);
};
const StyledTextBody = styled(TextBody) `
  max-width: 650px;
`;
const InsightContainer = styled(Box) `
  display: grid;
  grid-template-columns: 15px; 1fr;
  grid-gap: 6px;
  grid-auto-rows: auto;
  max-width: 550px;
`;
const StyledInsightIcon = styled(Icon) `
  grid-column: 1;
  grid-row: 1;
  margin-top: 3px;
`;
const InsightMessageContainer = styled(Box) `
  grid-column: 2;
  display: grid;
  row-gap: 5px;
`;
export const SendCodeContent = ({ status, targets, hostServices, isInPortal, logger, onSelectOTP, onSelectPushApprove, getOptionType, hasPushApproveAuthenticator, mobileApproveStatus, }) => {
    // Only show the step up content if the mobile approve status is defined and set to NOT_REGISTERED.
    // If the value is undefined the feature should be considered disabled and we should not show any content
    //
    // hasPushApproveAuthenticator and mobileApproveStatue === 'NOT_REGISTERED' should never conflict, checking anyways
    const shouldShowMobileApproveStepUpContent = !hasPushApproveAuthenticator && mobileApproveStatus === 'NOT_REGISTERED';
    React.useEffect(() => {
        logger === null || logger === void 0 ? void 0 : logger.track(TrackingEvent.TransmitSendCodeLoad);
        hostServices === null || hostServices === void 0 ? void 0 : hostServices.analytics.trackPageView({
            eventType: 'pageview',
            pageName: 'AOS:Login:Challenge',
        });
    }, []);
    // We don't show voice options since they are just duplicates of SMS options
    const displayTargets = targets
        .filter(({ _channel }) => {
        const type = getOptionType(_channel);
        return !(type === 'voice');
    })
        .sort(({ _channel: channel1 }, { _channel: channel2 }) => channel1 - channel2);
    const voiceTargets = targets.filter(({ _channel }) => {
        const type = getOptionType(_channel);
        return type === 'voice';
    });
    const [selectedType, setSelectedType] = React.useState(getOptionType(displayTargets[0]._channel));
    const [selectedRadioValue, setSelectedRadioValue] = React.useState('');
    const [selectedTarget, setSelectedTarget] = React.useState(displayTargets[0]);
    const hasSingleDisplayOption = displayTargets.length === 1;
    const sendCode = () => {
        logger === null || logger === void 0 ? void 0 : logger.track(TrackingEvent.TransmitSendCodeSubmit);
        if (selectedType === 'push approval') {
            onSelectPushApprove === null || onSelectPushApprove === void 0 ? void 0 : onSelectPushApprove();
        }
        else {
            onSelectOTP(selectedTarget);
        }
    };
    const onOptionSelectionChange = (id) => {
        const optionalTarget = displayTargets.find(target => id === getDeliveryOptionId(target));
        if (optionalTarget) {
            const type = getOptionType(optionalTarget._channel);
            // if selectedType is voice and optionalTarget is type sms, find voice target equivalent to set
            const shouldSetVoiceTarget = selectedType === 'voice' && type === 'text message';
            const target = shouldSetVoiceTarget
                ? voiceTargets.find(voiceTarget => optionalTarget._description === voiceTarget._description)
                : optionalTarget;
            if (target)
                setSelectedTarget(target);
            if (!shouldSetVoiceTarget)
                setSelectedType(type);
        }
    };
    const onMethodSelectionChange = (method) => {
        if (method !== selectedType) {
            setSelectedType(method);
        }
        const targetsWithSelectedType = method === 'voice' ? voiceTargets : displayTargets;
        const target = targetsWithSelectedType.find(phoneTarget => phoneTarget._description === selectedTarget._description);
        if (target)
            setSelectedTarget(target);
    };
    const initialValues = {
        loginType: '',
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(LoginHeading, { title: "Choose how you'll verify this login.", isInPortal: isInPortal }),
        React.createElement(Space, { mb: "md" }, !hasPushApproveAuthenticator ? (React.createElement(StyledTextBody, { size: "sm", tag: "p" }, "You can review and update your login settings in Profile after you're logged in.")) : (React.createElement(StyledTextBody, { size: "sm", tag: "p" }, "Select push approval to skip this step next time we need to verify you. You can always change your login settings in Profile after you're logged in. Reminder: Once you receive the code, don't share it with anyone."))),
        status === 'Error' && (React.createElement(AriaLive, null,
            React.createElement(TextBody, { size: "sm", tag: "p" }, "Please tell us where you want your security code sent."))),
        React.createElement(Forms, { formId: "submit", initialValues: initialValues, onSubmit: sendCode },
            React.createElement(Form, { id: "submit" },
                !(selectedType === 'email') && (React.createElement(PushTextOrVoiceSelection, { hasPushApproveAuthenticator: hasPushApproveAuthenticator, maskText: maskTarget(selectedTarget, getOptionType), hasSingleDisplayOption: hasSingleDisplayOption, setSelectedRadioValue: setSelectedRadioValue, onMethodSelectionChange: onMethodSelectionChange })),
                React.createElement(Box, null, !hasSingleDisplayOption &&
                    (selectedRadioValue === 'text message' ||
                        selectedRadioValue === 'voice') && (React.createElement(Space, { mb: "md" },
                    React.createElement(DeliveryMethodsOptions, { targets: displayTargets, getOptionType: getOptionType, onOptionSelectionChange: onOptionSelectionChange, selectedRadioValue: selectedRadioValue })))),
                shouldShowMobileApproveStepUpContent && (React.createElement(InsightContainer, { mb: "lg" },
                    React.createElement(StyledInsightIcon, { icon: muiLightbulb, fill: "plum", size: "md" }),
                    React.createElement(InsightMessageContainer, null,
                        React.createElement(TextBody, { tag: "p", size: "sm", weight: "bold" }, "Next time, get a push notification"),
                        React.createElement(TextBody, { size: "sm", tag: "p" },
                            "Get enhanced login security with this two-step verification that's just two taps away when you use the Ally app.",
                            ' ',
                            React.createElement(HowToSetUpPushApproveModal, null))))),
                React.createElement(Box, { display: "flex", flexDirection: ['column', 'column', 'row', 'row'], alignItems: "center", my: "sm" },
                    React.createElement(Space, { mr: [0, 0, 'sm', 'sm'], mb: ['sm', 'sm', 'none', 'none'] },
                        React.createElement(Button, { allytmln: "sendSecurityCode", text: "Next", type: "submit", variant: "primary", loading: status === 'Loading' })))))));
};
//# sourceMappingURL=SendCode.js.map